import axios, { type AxiosInstance, type AxiosResponse } from 'axios';
import axiosRetry, { isNetworkOrIdempotentRequestError } from 'axios-retry';
import { config } from '@/config';
import { createAuthProvider } from './auth/createAuthProvider';
import type {
  AmlReview,
  AmlReviewListResponse,
  AmlReviewResult,
  CompleteAmlReviewResponse,
  CreateHumanReviewPayload,
  UpdateHumanReviewPayload,
} from './aml-review/types';
import type { CardTokenListResponse } from './card-tokens/types';
import type { CardDetail, CardListResponse } from './cards/types';
import type { FundingSource, FundingSourceListResponse } from './funding-sources/types';
import type { Payment, PaymentAuthorizationRelease } from './payments/types';
import type {
  FrankieOneDocumentFullResponse,
  FrankieOneKycCheck,
  FrankieOneTenantRegion,
} from './FrankieOne/types';
import type { SumsubKycCheck, SumsubTenantRegion } from './Sumsub/types';
import type { Whoami } from './auth/types';

type ReleasePaymentAuthorizationOpts = {
  paymentId: string;
  release: PaymentAuthorizationRelease;
};

function latestByCreatedAt(results: AmlReviewResult[]): AmlReviewResult | null {
  return results.reduce<AmlReviewResult | null>(
    (latest, current) => (!latest || current.createdAt > latest.createdAt ? current : latest),
    null
  );
}

export function pickLatestAgentReview(results: AmlReviewResult[]): AmlReviewResult | null {
  return latestByCreatedAt(results.filter((result) => result.recordType === 'AmlAgentReview'));
}

export function pickLatestHumanReview(results: AmlReviewResult[]): AmlReviewResult | null {
  return latestByCreatedAt(results.filter((result) => result.recordType === 'AmlHumanReview'));
}

export class AmethystClient {
  readonly axiosInstance: AxiosInstance;

  constructor() {
    this.axiosInstance = axios.create({
      timeout: config.axiosTimeout,
    });

    axiosRetry(this.axiosInstance, {
      retries: 2,
      retryCondition: (e) =>
        isNetworkOrIdempotentRequestError(e) || !e.status || !e.response?.status,
      retryDelay: axiosRetry.exponentialDelay,
      shouldResetTimeout: true,
    });

    const authProvider = createAuthProvider({
      authType: config.authType,
      apiKey: config.apiKey,
      apiSecret: config.apiSecret,
    });
    this.axiosInstance.interceptors.request.use((request) => authProvider.applyTo(request));
  }

  // Every endpoint goes through here, so the call shape lives in one place.
  // Failures are left alone — naming and handling them belongs to the caller.
  private async request<T>({
    method,
    url,
    data,
    params,
    responseType,
  }: {
    method: 'GET' | 'POST';
    url: string;
    data?: unknown;
    params?: unknown;
    responseType?: 'blob';
  }): Promise<AxiosResponse<T>> {
    return this.axiosInstance.request<T>({ method, url, data, params, responseType });
  }

  async getWhoami(): Promise<Whoami> {
    const { data } = await this.request<Whoami>({
      method: 'GET',
      url: '/api/whoami',
    });
    return data;
  }

  async postLogout(): Promise<void> {
    await this.request({
      method: 'POST',
      url: '/auth/logout',
    });
  }

  async getAmlReviews(params?: {
    status?: string;
    sort?: 'asc' | 'desc';
    limit?: number;
    cursor?: string;
    region?: string;
    partnerAccountId?: string;
    kycProvider?: string;
    accountId?: string;
    externalProfileId?: string;
    since?: string;
  }): Promise<AmlReviewListResponse> {
    const { data } = await this.request<AmlReviewListResponse>({
      method: 'GET',
      url: '/api/admin/aml-reviews',
      params,
    });
    return data;
  }

  async getAmlReviewById(amlReviewId: string): Promise<AmlReview> {
    const { data } = await this.request<AmlReview>({
      method: 'GET',
      url: `/api/admin/aml-reviews/${amlReviewId}`,
    });
    return data;
  }

  async getAmlReviewResults(amlReviewId: string): Promise<AmlReviewResult[]> {
    const { data } = await this.request<AmlReviewResult[]>({
      method: 'GET',
      url: `/api/admin/aml-reviews/${amlReviewId}/results`,
    });
    return data;
  }

  async getAmlReviewResult(amlReviewId: string): Promise<AmlReviewResult | null> {
    return pickLatestAgentReview(await this.getAmlReviewResults(amlReviewId));
  }

  // Fetches an attachment's bytes as a blob rather than letting an <img>
  // point at the URL directly — auth here is X-Api-Key/X-Api-Secret headers
  // applied by the request interceptor, which a plain <img src> can't carry.
  // The caller is responsible for URL.createObjectURL/revokeObjectURL.
  async getAmlReviewAttachment(
    amlReviewId: string,
    fileId: string
  ): Promise<{ blob: Blob; mimeType?: string }> {
    const response = await this.request<Blob>({
      method: 'GET',
      url: `/api/admin/aml-reviews/${amlReviewId}/attachments/${fileId}`,
      responseType: 'blob',
    });
    const contentType = response.headers['content-type'];
    return {
      blob: response.data,
      mimeType: typeof contentType === 'string' ? contentType : undefined,
    };
  }

  // FrankieOne's v1 API has no endpoint that returns a single scan's bytes
  // — the only one that returns scan data at all is
  // document/{documentId}/full, which returns every scan on that document
  // as base64 JSON.
  async getAmlReviewAttachmentJson(
    amlReviewId: string,
    fileId: string
  ): Promise<FrankieOneDocumentFullResponse> {
    const { data } = await this.request<FrankieOneDocumentFullResponse>({
      method: 'GET',
      url: `/api/admin/aml-reviews/${amlReviewId}/attachments/${fileId}/json`,
    });
    return data;
  }

  async getLatestHumanReview(amlReviewId: string): Promise<AmlReviewResult | null> {
    return pickLatestHumanReview(await this.getAmlReviewResults(amlReviewId));
  }

  async getFrankieOneKycCheck(
    tenantRegion: FrankieOneTenantRegion,
    externalId: string
  ): Promise<FrankieOneKycCheck> {
    const { data } = await this.request<FrankieOneKycCheck>({
      method: 'GET',
      url: `/api/admin/frankie-one/${tenantRegion}/${externalId}/kyc-check`,
    });
    return data;
  }

  async getSumsubKycCheck(
    tenantRegion: SumsubTenantRegion,
    externalId: string
  ): Promise<SumsubKycCheck> {
    const { data } = await this.request<SumsubKycCheck>({
      method: 'GET',
      url: `/api/admin/sumsub/${tenantRegion}/${externalId}/kyc-check`,
    });
    return data;
  }

  async createHumanReview(
    agentReviewResultId: string,
    payload: CreateHumanReviewPayload
  ): Promise<AmlReviewResult> {
    const { data } = await this.request<AmlReviewResult>({
      method: 'POST',
      url: `/api/admin/aml-review-results/${agentReviewResultId}/create-human-review`,
      data: payload,
    });
    return data;
  }

  async updateHumanReview(
    humanReviewResultId: string,
    payload: UpdateHumanReviewPayload
  ): Promise<AmlReviewResult> {
    const { data } = await this.request<AmlReviewResult>({
      method: 'POST',
      url: `/api/admin/aml-review-results/${humanReviewResultId}/update-human-review`,
      data: payload,
    });
    return data;
  }

  async completeAmlReview(
    amlReviewId: string,
    completedBy: string
  ): Promise<CompleteAmlReviewResponse> {
    const { data } = await this.request<CompleteAmlReviewResponse>({
      method: 'POST',
      url: `/api/admin/aml-reviews/${amlReviewId}/complete`,
      data: { completedBy },
    });
    return data;
  }

  async listCardTokens(
    cardId: string,
    params?: { limit?: number; cursor?: string }
  ): Promise<CardTokenListResponse> {
    const { data } = await this.request<CardTokenListResponse>({
      method: 'GET',
      url: `/api/cards/${encodeURIComponent(cardId)}/tokens`,
      params,
    });
    return data;
  }

  async listCards(
    accountId: string,
    params?: { limit?: number; cursor?: string }
  ): Promise<CardListResponse> {
    const { data } = await this.request<CardListResponse>({
      method: 'GET',
      url: `/api/accounts/${encodeURIComponent(accountId)}/cards`,
      params,
    });
    return data;
  }

  async getCard(cardId: string): Promise<CardDetail> {
    const { data } = await this.request<CardDetail>({
      method: 'GET',
      url: `/api/cards/${encodeURIComponent(cardId)}`,
    });
    return data;
  }

  async getPayment(paymentId: string): Promise<Payment> {
    const { data } = await this.request<Payment>({
      method: 'GET',
      url: `/api/payments/${encodeURIComponent(paymentId)}`,
    });
    return data;
  }

  async releasePaymentAuthorization(
    opts: ReleasePaymentAuthorizationOpts
  ): Promise<PaymentAuthorizationRelease> {
    const { data } = await this.request<PaymentAuthorizationRelease>({
      method: 'POST',
      url: `/api/payments/${encodeURIComponent(opts.paymentId)}/release-authorization`,
      data: opts.release,
    });
    return data;
  }

  async listFundingSources(accountId: string): Promise<FundingSourceListResponse> {
    const { data } = await this.request<FundingSourceListResponse>({
      method: 'GET',
      url: `/api/accounts/${encodeURIComponent(accountId)}/funding-sources`,
    });
    return data;
  }

  // Singular "funding-source" is amethyst's own path, unlike its other
  // funding-source routes.
  async getFundingSource(fundingSourceId: string): Promise<FundingSource> {
    const { data } = await this.request<FundingSource>({
      method: 'GET',
      url: `/api/funding-source/${encodeURIComponent(fundingSourceId)}`,
    });
    return data;
  }
}
